import { Injectable } from '@angular/core';
import {AppSettings} from '../../app.settings';
import {Dictionary} from 'highcharts';
import {Restangular} from 'ngx-restangular';
import { Vendor } from '../vendors/vendors.service';

export interface PartnerInviteEmailAppConfig {
   downloadUrl?: string;
   qrCodeUrl?: string;
}

export interface PartnerInviteEmailConfig {
   mode?: 'web' | 'app';
   app?: PartnerInviteEmailAppConfig;
}

export type PartnerConfigJson = Dictionary<any> & {
   inviteEmail?: PartnerInviteEmailConfig;
   retainBrandAfterTeamRemoval?: boolean;
};

export interface Partner {
   partnerId?: string;
   name: string;
   shortName: string;
   logoUrl: string;
   horizontalLogoUrl: string;
   lightLogoUrl: string;
   horizontalLogoOnWhite: string;
   verticaLogoOnWhite: string;
   redirectUrl: string;
   configjson?: PartnerConfigJson;
   signupDisabled: boolean;
   tagline: string;
   ourlyPrimaryColor: string;
   ourlySecondaryColor: string;
   wallitTertiaryColor?: string;
   wallitGradientNEColor: string;
   wallitGradientSWColor: string;
}

export interface SignupBrandContext {
   brandSlug?: string;
   brand?: Partner;
}

@Injectable({
    providedIn: 'root'
})

export class PartnersService {

    private brand;
    private isBranded = false;

    private defaultBrand = {
        name: 'Wallit',
        tagline: 'The Workplace Wallet',
        logoUrl: 'assets/logo/Wallit - Full Logo.svg',
        ourlyPrimaryColor: '#FF224A',
        ourlySecondaryColor: '#171A51',
        wallitTertiaryColor: '#3AE3A0',
        wallitGradientSWColor: '#FF005D',
        wallitGradientNEColor: '#FF4139',
        lightLogoUrl: '/assets/logo/Wallit - Brandmark.svg',
        verticalLogoOnWhiteUrl: 'assets/logo/Wallit - Full Logo - White.png',
        horizontalLogoUrl: '/assets/logo/Wallit - Horizontal - White All.png',
        horizontalLogoOnWhiteUrl: '/assets/logo/Wallit - Horizontal.svg',
        configjson: { bands: [], content: {ui: {giftcards: true, products: true}} },
        redirectUrl: 'wallit.app'
    };

    constructor(
        private restangular: Restangular,
    ) {
        this.brand = this.defaultBrand;
    }

    async init(): Promise<any> {
        const subdomain = await this.getSubdomain();
        return this.getAllPartners().then((data: Array<any>) => {
            const ourBrand = data.find(brand => brand.redirectUrl && brand.redirectUrl.split('.')[0] === subdomain);
            const wallitBrandInfo = data.find(brand => brand.name === 'Wallit');
            if (wallitBrandInfo) {
                this.defaultBrand.configjson = wallitBrandInfo.configjson;
            }
            this.setBrand(subdomain ? ourBrand : undefined);
            return data;
        });
    }


    getAllPartners(): Promise<Partner[]> {
        return this.restangular.one('public/partners').getList().toPromise() as Promise<Partner[]>;
    }

    lookupSignupBrandContext(email: string): Promise<SignupBrandContext | null> {
        return this.restangular
          .one('public/signup-brand-context')
          .get({ email })
          .toPromise()
          .then((context: SignupBrandContext) => context || null)
          .catch((error: any) => {
              if (error?.status === 204) {
                  return null;
              }
              return Promise.reject(error);
          });
    }

    createPartner(partner: Partner): Promise<Partner> {
        return this.restangular.one('admin/partners').customPOST(partner).toPromise() as Promise<Partner>;
    }

    deletePartner(partner: Partner): Promise<Partner> {
      return this.restangular.one('admin/partners').one(partner.partnerId).customDELETE().toPromise() as Promise<Partner>;
    }

    updatePartner(partner: Partner): Promise<Partner> {
      return this.restangular.one('admin/partners').one(partner.partnerId).customPUT(partner).toPromise() as Promise<Partner>;
    }

    public changePartnerLogo(partnerId: string, logo: any, logoType: 'avatar' | 'lightlogo' | 'horizontallogo' | 'horizontallightlogo'): Promise<void> {
      return this.restangular.one(`admin/partners`).one(partnerId, logoType).customPUT(logo).toPromise();
    }

    public uploadInviteQr(partnerId: string, qrImage: any): Promise<{ url: string }> {
      return this.restangular.one('admin/partners').one(partnerId, 'inviteqr').customPUT(qrImage).toPromise() as Promise<{ url: string }>;
    }

    private async getSubdomain(): Promise<string> {
        const overrideBrand = await AppSettings.getBrandOverride();
        return overrideBrand ? overrideBrand : window.location.host.split('.')[0];
    }

    private setBrand(brand: any): void {
        if (brand) {
            this.isBranded = true;
            this.brand = brand;
        }
        const styles = document.documentElement.style;
        styles.setProperty('--ion-color-primary', this.brand.ourlyPrimaryColor);
        styles.setProperty('--ion-color-secondary', this.brand.ourlySecondaryColor);
        styles.setProperty('--app-color-tertiary', this.brand.wallitTertiaryColor);
        styles.setProperty('--app-color-gradientsw', this.brand.wallitGradientSWColor);
        styles.setProperty('--app-color-gradientne', this.brand.wallitGradientNEColor);
        styles.setProperty('--app-color-default-primary', this.defaultBrand.ourlyPrimaryColor);
        styles.setProperty('--app-color-default-secondary', this.defaultBrand.ourlySecondaryColor);
        styles.setProperty('--app-color-default-tertiary', this.defaultBrand.wallitTertiaryColor);
        styles.setProperty('--app-color-default-gradientsw', this.defaultBrand.wallitGradientSWColor);
        styles.setProperty('--app-color-default-gradientne', this.defaultBrand.wallitGradientNEColor);
    }

}
